/* global axios */
import ApiClient from './ApiClient';

class TablesAPI extends ApiClient {
  constructor() {
    super('tables', { accountScoped: true });
  }

  // Columns, rows, saved queries and grants are nested under a table, so they
  // are reached from here rather than as their own top-level clients.
  columns(tableId) {
    return `${this.url}/${tableId}/columns`;
  }

  getColumns(tableId) {
    return axios.get(this.columns(tableId));
  }

  createColumn(tableId, column) {
    return axios.post(this.columns(tableId), { column });
  }

  updateColumn(tableId, columnId, column) {
    return axios.patch(`${this.columns(tableId)}/${columnId}`, { column });
  }

  deleteColumn(tableId, columnId) {
    return axios.delete(`${this.columns(tableId)}/${columnId}`);
  }

  getRows(tableId, params) {
    return axios.get(`${this.url}/${tableId}/rows`, { params });
  }

  createRow(tableId, data) {
    return axios.post(`${this.url}/${tableId}/rows`, { row: { data } });
  }

  updateRow(tableId, rowId, data) {
    return axios.patch(`${this.url}/${tableId}/rows/${rowId}`, {
      row: { data },
    });
  }

  deleteRow(tableId, rowId) {
    return axios.delete(`${this.url}/${tableId}/rows/${rowId}`);
  }

  rowHistory(tableId, rowId) {
    return axios.get(`${this.url}/${tableId}/rows/${rowId}/history`);
  }

  getSavedQueries(tableId) {
    return axios.get(`${this.url}/${tableId}/saved_queries`);
  }

  createSavedQuery(tableId, savedQuery) {
    return axios.post(`${this.url}/${tableId}/saved_queries`, {
      saved_query: savedQuery,
    });
  }

  updateSavedQuery(tableId, id, savedQuery) {
    return axios.patch(`${this.url}/${tableId}/saved_queries/${id}`, {
      saved_query: savedQuery,
    });
  }

  deleteSavedQuery(tableId, id) {
    return axios.delete(`${this.url}/${tableId}/saved_queries/${id}`);
  }

  getGrants(tableId) {
    return axios.get(`${this.url}/${tableId}/grants`);
  }

  getGrantSubjects(tableId) {
    return axios.get(`${this.url}/${tableId}/grants/subjects`);
  }

  createGrant(tableId, grant) {
    return axios.post(`${this.url}/${tableId}/grants`, { grant });
  }

  updateGrant(tableId, id, grant) {
    return axios.patch(`${this.url}/${tableId}/grants/${id}`, { grant });
  }

  deleteGrant(tableId, id) {
    return axios.delete(`${this.url}/${tableId}/grants/${id}`);
  }

  // Multipart: the file plus a file-header -> column-key mapping.
  import(tableId, file, mapping) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(mapping).forEach(([header, columnKey]) => {
      if (columnKey) formData.append(`mapping[${header}]`, columnKey);
    });

    return axios.post(`${this.url}/${tableId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // The header row is read server-side because an .xlsx is a zip archive
  // the browser cannot parse. Nothing is written by this call.
  importHeaders(tableId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return axios.post(`${this.url}/${tableId}/import_headers`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export default new TablesAPI();
